import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ghostRowIndex, usePeopleOrder, useProjectOrder } from '../shared/order';
import { confirmDialog } from '@core/ui/confirm';
import { ScreenLoading } from '@core/ui/Loading';
import { Eraser, Highlighter, SquarePen, Trash2 } from 'lucide-react';
import { newDraft, planNewTask } from '../shared/cardEdits';
import { useAuth } from '@core/auth/store';
import { useCanWrite } from '@core/auth/access';
import { data, useData, useSetting, useTable } from '@core/store';
import { addDays, daysBetween, mondayOf, sundayOf, today } from '@core/dates';
import { BAR_HEIGHT, LABEL_WIDTH, assignSubRows, barGeometry, barHeightFor, snapDays, gridOf } from '../shared/geometry';
import { useTitleMeasurer } from '../shared/textWidth';
import { SplitGrid, type SplitRow } from '../shared/SplitGrid';
import { reorderGhostRow, withGhostRow } from '../shared/RowActions';
import { RangeControls } from '../shared/RangeControls';
import { defaultRange, type Range } from '../shared/range';
import { ScreenToolbar, ViewMenu, barViewItems } from '../shared/Toolbar';
import { SpotlightChip, jumpToTask, spotBox, useSpotlight } from '../shared/spotlight';
import type { BarLook, BarMarks } from '../shared/TaskBar';
import { PersonRowGrid, PersonRowLabel, type Drag, type PeopleHint, type PeopleQuick, type Preview } from './PersonRow';
import { applyPlanned } from '../shared/apply';
import { assigneesNow, freshOf, rowKeyOf, rowTasksNow, taskNow, useAssignees, useAttention, type Task, tasksNow } from '../shared/data';
import { TaskModal } from '../shared/TaskModal';
import { planResize } from '../shared/planner';
import { SelectionChip, applyGroupMove, groupShiftOf, taskMenu, useGroupActions, useMarquee, useSelection } from '../shared/selection';
import { keyEnd, keyStart, resizeTarget, snapStart } from '../shared/stepping';
import {
  usePendingIds, useDuplicate, useRowReorder, useTaskActions, useTaskCard, useTaskDrag, useTaskKeys, useToast, useTodayX, useHolidayDays, roadmapGroupPlan, refocus,
} from '../shared/interaction';
import { UNASSIGNED, countInRange, newVacationDraft, peopleRows, personRowShape, planReassign, tasksByPerson as groupByPerson, vacationDraftOf, type PersonRow, type Vacation, type VacationDraft } from './planner';
import { VacationModal } from './VacationModal';
import { useContextBand } from '../shared/ContextBand';
import { useOpenQuestionCounts } from '../shared/questions';
import type { MenuItem } from '@core/ui/Menu';
import type { Row } from '@core/store/model';
import { planConfirm } from '../shared/series';
import { EventModal, eventDraftOf, newEventDraft, type EventDraft } from '../shared/EventModal';

const focusOf = (id: string, person: string | null) => `[data-task="${id}"][data-in="${person ?? UNASSIGNED}"]`;

export function PeopleScreen() {
  const status = useData((s) => s.status);
  const [range, setRange] = useSetting<Range>('projects.range', defaultRange()); // диапазон один на роадмап, не два (спека People 2026-09-07)
  const [hidden, setHidden] = useSetting<string[]>('people.hidden', []);
  const [onlyMe, setOnlyMe] = useSetting<boolean>('people.only_me', false);
  // как в старом People: один цвет на человека; цвета исполнителей — чтобы видеть, кто с кем
  const [byPerson, setByPerson] = useSetting<boolean>('people.color_by_person', false);
  const profile = useAuth((s) => s.profile);
  const people = useTable('person');
  const tasks = useTable('task');
  const assigneeRows = useTable('task_assignee');
  const vacations = useTable('vacation');
  const projects = useTable('project');
  const { colors, names } = useAssignees();
  const attention = useAttention();
  const pendingIds = usePendingIds();
  const { fail } = useToast();
  const { draft, setDraft, openTask, closeModal, cardTab } = useTaskCard();
  const [badges, setBadges] = useSetting<boolean>('bars.badges', true);
  const [releaseMarks, setReleaseMarks] = useSetting<boolean>('bars.release_marks', true);
  const [fullTitles, setFullTitles] = useSetting<boolean>('bars.full_titles', false);
  const measure = useTitleMeasurer();
  const questionCounts = useOpenQuestionCounts();
  const myPersonId = profile?.person_id ?? null;
  const canWrite = useCanWrite();
  useEffect(() => { data.updatePresence({ screen: 'people' }); }, []);

  const grid = useMemo(() => gridOf(range.from, range.to), [range]);
  const holidays = useHolidayDays(grid);
  const todayX = useTodayX(grid);
  const [eventDraft, setEventDraft] = useState<EventDraft | null>(null);
  const [vacDraft, setVacDraft] = useState<VacationDraft | null>(null);
  // новая задача из строки человека: проекта у строки нет — берём последний выбранный, сначала первый по порядку
  const projectOrder = useProjectOrder();
  const projectOptions = useMemo(() => projectOrder.rows.map((p) => ({ id: p.id, name: p.name, color: p.color })), [projectOrder.rows]);
  const [quickProject, setQuickProject] = useState<string | null>(null);
  const projectFor = quickProject && projectOptions.some((p) => p.id === quickProject) ? quickProject : (projectOptions[0]?.id ?? '');
  const [hint, setHint] = useState<PeopleHint | null>(null);
  const [quick, setQuick] = useState<PeopleHint | null>(null);
  const onEvent = useCallback((e: Parameters<typeof eventDraftOf>[0] | null, at = today()) => setEventDraft(e ? eventDraftOf(e) : newEventDraft(mondayOf(at), sundayOf(at))), []);
  const onConfirmEvent = useCallback((e: Row<'event'>) => { void data.applyChanges(planConfirm(e), { kind: 'confirm_event' }).then((r) => { if (!r.ok) fail(r.error); }); }, [fail]);
  const actions = useTaskActions(fail);
  const [hiddenLanes, setHiddenLanes] = useSetting<string[]>('context.hidden', []);
  const gridRef = useRef<HTMLDivElement>(null);
  const spotlight = useSpotlight();
  // прыжок к задаче: показать скрытых исполнителей и снять «Only me», если задача не моя
  const jump = useCallback((t: Task) => jumpToTask(gridRef, t.id, () => {
    const ids = [...assigneeRows.values()].filter((a) => a.task_id === t.id).map((a) => a.person_id);
    if (ids.some((id) => hidden.includes(id))) setHidden(hidden.filter((id) => !ids.includes(id)));
    if (onlyMe && !(myPersonId && ids.includes(myPersonId))) setOnlyMe(false);
  }), [assigneeRows, hidden, setHidden, onlyMe, setOnlyMe, myPersonId]);
  const jumpById = useCallback((id: string) => { const t = taskNow(id); if (t) jump(t); }, [jump]);
  const pinnedOpts = useMemo(() => ({ colors, onOpen: openTask, onUnpin: actions.pin, onJump: jump }), [colors, openTask, actions.pin, jump]);
  const context = useContextBand(grid, { todayX, onEvent, onConfirmEvent, pinned: pinnedOpts, spotlight, hidden: hiddenLanes, setHidden: setHiddenLanes });

  // ---------------------------------------------------------------- строки

  const peopleOrder = usePeopleOrder();
  const rows: PersonRow[] = useMemo(() => peopleRows(peopleOrder.rows, { hidden, onlyMe, myPersonId }), [peopleOrder.rows, hidden, onlyMe, myPersonId]);
  const tasksByPerson = useMemo(() => groupByPerson(assigneeRows.values(), tasks), [assigneeRows, tasks]);
  const vacationsByPerson = useMemo(() => {
    const m = new Map<string, Vacation[]>();
    for (const v of vacations.values()) m.set(v.person_id, [...(m.get(v.person_id) ?? []), v]);
    return m;
  }, [vacations]);
  const projectName = useCallback((t: Task) => projects.get(t.project_id)?.name ?? '', [projects]);

  const latest = useRef({ rows });
  latest.current = { rows };
  const visibleTasksOf = useCallback((personId: string) => (tasksByPerson.get(personId) ?? []).filter((t) => barGeometry(t, grid) !== null), [tasksByPerson, grid]);
  const visibleIds = useMemo(() => new Set(rows.flatMap((r) => visibleTasksOf(r.id).map((t) => t.id))), [rows, visibleTasksOf]);
  const selection = useSelection(visibleIds, tasksNow);
  const { marquee } = useMarquee(gridRef, selection);

  // ---------------------------------------------------------------- перетаскивание бара

  const targetFor = useCallback((d: Drag, clientX: number, clientY: number, dx: number): Preview | null => {
    const fresh = freshOf(d.task);
    const days = snapDays(dx, 1);
    if (d.kind !== 'move') return { kind: 'resize', ...resizeTarget(fresh, d.kind, days, d.day) };
    if (d.group.length > 1) {
      // группа: только время, строка у каждого своя
      const start = snapStart(addDays(fresh.start_date, days), d.day);
      return { kind: 'move', rowId: d.origin ?? UNASSIGNED, start_date: start, end_date: addDays(start, daysBetween(fresh.start_date, fresh.end_date)) };
    }
    const rowEl = document.elementFromPoint(clientX, clientY)?.closest<HTMLElement>('[data-person-row]');
    const row = latest.current.rows.find((r) => r.id === rowEl?.dataset['personRow']);
    if (!row) return null;
    const start = snapStart(addDays(fresh.start_date, days), d.day);
    return { kind: 'move', rowId: row.id, start_date: start, end_date: addDays(start, daysBetween(fresh.start_date, fresh.end_date)) };
  }, []);

  const finishDrag = useCallback(async (d: Drag, clientX: number, clientY: number, dx: number) => {
    const target = targetFor(d, clientX, clientY, dx);
    if (!target) return;
    if (target.kind === 'move' && d.group.length > 1) return applyGroupMove(d.task, d.group, target.start_date, fail);
    const r = await applyPlanned(() => {
      const t = freshOf(d.task);
      if (target.kind === 'resize') {
        const side = d.kind === 'resize-end' ? 'end' : 'start';
        return { changes: planResize(t, side, side === 'end' ? target.end_date : target.start_date, rowTasksNow(rowKeyOf(t.project_id, t.subproject_id))), op: { kind: 'resize_task', task_id: t.id, side } };
      }
      const toPerson = target.rowId === UNASSIGNED ? null : target.rowId;
      return { changes: planReassign(t, assigneesNow(), d.origin, toPerson, target.start_date), op: { kind: 'reassign_task', task_id: t.id, from: d.origin, to: toPerson } };
    });
    if (!r.ok) fail(r.error);
  }, [targetFor, fail]);

  const { dragPreview, onBarPointerDown } = useTaskDrag<string | null, Preview>({ gridRef, rowAttr: 'data-person-row', targetFor, onDrop: finishDrag, focusSelector: (d) => focusOf(d.task.id, d.origin), selection });
  const duplicateOne = useDuplicate(fail, useCallback((id: string) => refocus(`[data-task="${id}"]`), []));
  const group = useGroupActions(selection, fail, { duplicate: duplicateOne, backlog: actions.backlog, remove: actions.remove });
  const menuFor = useCallback((t: Task): MenuItem[] => taskMenu(t, { openTask, pin: actions.pin, highlight: spotlight, group, canWrite }), [openTask, group, actions, spotlight, canWrite]);
  // отпуск: правка, подсветка, удаление с вопросом
  const vacationMenu = useCallback((v: Row<'vacation'>): MenuItem[] => [
    { label: canWrite ? 'Edit vacation' : 'Open vacation', onSelect: () => setVacDraft(vacationDraftOf(v)), icon: SquarePen },
    { label: spotlight.is('vacation', v.id) ? 'Remove the highlight' : 'Highlight', onSelect: () => spotlight.toggle({ kind: 'vacation', id: v.id }), icon: spotlight.is('vacation', v.id) ? Eraser : Highlighter },
    ...(canWrite ? ['sep' as const, { label: 'Delete vacation', danger: true, icon: Trash2, onSelect: () => void (async () => {
      const who = people.get(v.person_id)?.name ?? 'this person';
      if (!(await confirmDialog({ title: 'Delete vacation', text: <>Delete <b>{who}</b>'s vacation {v.start_date} – {v.end_date}? It stays in the history.</>, yes: 'Delete', danger: true }))) return;
      const r = await data.applyChanges([{ table: 'vacation', id: v.id, delete: true }], { kind: 'delete_vacation', person_id: v.person_id });
      if (!r.ok) fail(r.error);
    })() }] : []),
  ], [canWrite, spotlight, people, fail]);
  // быстрая задача в строке человека: неделя под курсором, исполнитель — человек строки, проект — в форме
  const quickDraft = useCallback((h: PeopleHint, title: string, assignees: string[]) => (projectFor ? { ...newDraft(projectFor, null, h.start_date), title, assignees } : null), [projectFor]);
  const quickFor = useCallback((h: PeopleHint): PeopleQuick => ({
    hint: h,
    project: { value: projectFor, options: projectOptions, onChange: setQuickProject },
    onCancel: () => setQuick(null),
    onFull: (title, assignees) => { setQuick(null); const d = quickDraft(h, title, assignees); if (d) setDraft(d); },
    onCreate: (title, assignees) => {
      const d = quickDraft(h, title, assignees);
      setQuick(null);
      if (!d) return;
      const { taskId, changes } = planNewTask(d, rowTasksNow(rowKeyOf(d.project_id, null)), () => crypto.randomUUID());
      void data.applyChanges(changes, { kind: 'create_task', task_id: taskId }).then((r) => { if (!r.ok) fail(r.error); else refocus(`[data-task="${taskId}"]`); });
    },
  }), [projectFor, projectOptions, quickDraft, setDraft, fail]);
  const onBarKey = useTaskKeys<string | null>({
    openTask, duplicate: group.duplicate, pin: actions.pin, remove: group.remove, fail, focusSelector: focusOf, groupOf: selection.groupOf, planGroup: roadmapGroupPlan,
    plan: (taskId, { days, rows: dRows, grow }, person) => {
      const t = taskNow(taskId);
      if (!t) return null;
      if (grow !== 0) return { planned: { changes: planResize(t, 'end', keyEnd(t.end_date, grow), rowTasksNow(rowKeyOf(t.project_id, t.subproject_id))), op: { kind: 'resize_task', task_id: t.id, side: 'end' } }, ctx: person };
      const list = latest.current.rows;
      const idx = list.findIndex((r) => r.id === (person ?? UNASSIGNED));
      const row = list[Math.max(0, Math.min(list.length - 1, idx + dRows))];
      const toPerson = !row || row.unassigned ? null : row.id;
      return { planned: { changes: planReassign(t, assigneesNow(), person, toPerson, keyStart(t.start_date, days)), op: { kind: 'reassign_task', task_id: t.id, from: person, to: toPerson } }, ctx: toPerson };
    },
  });

  // ---------------------------------------------------------------- перестановка людей

  const { rowDrag, onHandlePointerDown } = useRowReorder({
    gridRef, rowAttr: 'data-person-row',
    targetIdOf: (id) => (id === UNASSIGNED ? null : id),
    blockSelector: (id) => `[data-person-row="${id}"]`,
    visibleIds: () => latest.current.rows.filter((r) => !r.unassigned).map((r) => r.id),
    onDrop: (fromId, anchorId) => void peopleOrder.reorder(fromId, anchorId).catch((e: Error) => fail(e.message)),
  });

  // ---------------------------------------------------------------- отрисовка

  if (status !== 'ready') return <ScreenLoading />;

  const hiddenPeople = [...people.values()].filter((p) => hidden.includes(p.id));
  const groupShift = groupShiftOf(dragPreview);
  const marks: BarMarks = { colors, names, attention, pendingIds, questions: questionCounts, selected: selection.ids };
  const look: BarLook = { barH: BAR_HEIGHT, wrap: false, badges, releaseMarks, days: !!dragPreview?.day, holidays, spot: spotlight.range, spotBox: spotBox(spotlight.range, grid) };

  const splitRows: SplitRow[] = rows.map((row) => {
    const rowTasks = visibleTasksOf(row.id);
    const barH = fullTitles ? barHeightFor(rowTasks, grid, measure) : BAR_HEIGHT;
    const sub = assignSubRows(rowTasks);
    const previewHere = dragPreview?.preview?.kind === 'move' && dragPreview.preview.rowId === row.id;
    const isSource = !!dragPreview && (dragPreview.origin ?? UNASSIGNED) === row.id;
    const hintHere = hint?.rowId === row.id || quick?.rowId === row.id;
    const extraSlot = (previewHere && !isSource) || hintHere;
    const vacs = (row.unassigned ? [] : (vacationsByPerson.get(row.id) ?? [])).filter((v) => barGeometry(v, grid) !== null);
    const { subCount, strip, height } = personRowShape({ subRows: sub.values(), taskCount: rowTasks.length, extraSlot, hasVacations: vacs.length > 0, barH });
    const taskCount = countInRange(tasksByPerson.get(row.id) ?? [], range.from, range.to);
    return {
      key: row.id,
      height,
      className: `border-b border-gray-200 ${rowDrag?.id === row.id ? 'opacity-50' : ''}`,
      labelClassName: 'bg-gray-100',
      label: <PersonRowLabel row={row} taskCount={taskCount} onHandle={(e) => onHandlePointerDown(row.id, e)} onHide={() => setHidden([...hidden, row.id])} onAddVacation={() => setVacDraft(newVacationDraft(row.id))} />,
      gridAttrs: { 'data-person-row': row.id },
      gridClassName: previewHere ? 'bg-blue-50' : 'bg-white',
      grid: (
        <PersonRowGrid
          row={row} tasks={rowTasks} sub={sub} strip={strip} previewSlot={extraSlot ? subCount - 1 : (dragPreview ? (sub.get(dragPreview.task.id) ?? 0) : 0)} vacations={vacs}
          grid={grid} todayX={todayX} marks={marks} look={{ ...look, barH, wrap: barH > BAR_HEIGHT }} drag={dragPreview} groupShift={groupShift} byPerson={byPerson} projectName={projectName}
          onBarPointerDown={onBarPointerDown} onOpen={openTask} onKey={onBarKey} menuFor={menuFor} onVacation={(v) => setVacDraft(vacationDraftOf(v))} vacationMenu={vacationMenu}
          hint={hint?.rowId === row.id ? hint : null} onHint={setHint} quick={quick?.rowId === row.id ? quickFor(quick) : null}
          onQuickAt={setQuick}
          onNewAt={(date) => { if (projectFor) setDraft({ ...newDraft(projectFor, null, date), assignees: row.unassigned ? [] : [row.id] }); }}
          onVacationAt={(date) => { if (!row.unassigned) setVacDraft(newVacationDraft(row.id, date)); }}
        />
      ),
    };
  });

  // перестановка человека: строка-призрак с его именем на месте приземления; Unassigned всегда последний
  const dragged = rowDrag ? rows.find((r) => r.id === rowDrag.id) : undefined;
  const personIds = rows.filter((r) => !r.unassigned).map((r) => r.id);
  const ghostAt = ghostRowIndex(rowDrag, personIds, (id) => rows.findIndex((r) => r.id === id), personIds.length);
  const bodyRows = dragged ? withGhostRow(splitRows, ghostAt, reorderGhostRow(dragged.name, dragged.color)) : splitRows;

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow border border-gray-300 overflow-hidden">
      <ScreenToolbar
        when={<RangeControls range={range} onChange={setRange} onReset={() => setRange(null)} />}
        view={<ViewMenu items={[
            ...barViewItems({ fullTitles: [fullTitles, setFullTitles], badges: [badges, setBadges], releaseMarks: [releaseMarks, setReleaseMarks] }),
            { key: 'colours', label: 'Person colours', title: 'One colour per person, as in the old People View; off — colours of all assignees', checked: byPerson, onChange: setByPerson },
            { key: 'me', label: 'Only me', title: myPersonId ? 'Show only my row' : 'Your account is not linked to a person yet — ask an admin', checked: onlyMe && !!myPersonId, onChange: setOnlyMe, disabled: !myPersonId },
          ]} hidden={{ items: hiddenPeople, lanes: context.hiddenLanes, rowsLabel: 'People', onShow: (id) => setHidden(hidden.filter((x) => x !== id)), onShowLane: (id) => setHiddenLanes(hiddenLanes.filter((k) => k !== id)), onShowAll: () => { setHidden([]); setHiddenLanes([]); } }} />}
        status={<><SelectionChip selection={selection} /><SpotlightChip spotlight={spotlight} grid={grid} onJump={jumpById} /></>}
      />

      <SplitGrid
        gridRef={gridRef} grid={grid} labelWidth={LABEL_WIDTH} cornerLabel="Person"
        rows={bodyRows} scrollKey="people" cursor={dragPreview || rowDrag ? 'grabbing' : undefined} context={context}
      />


      {draft && <TaskModal draft={draft} allTasks={tasks} onClose={closeModal} initialTab={cardTab} />}
      {vacDraft && <VacationModal draft={vacDraft} onClose={() => setVacDraft(null)} />}
      {eventDraft && <EventModal draft={eventDraft} onClose={() => setEventDraft(null)} />}
      {marquee}
    </div>
  );
}
